import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { IgniteChakraModal } from "../../Modal/IgniteChakraModal";
import { HiOutlineCalendar } from "react-icons/hi";
import { Organization } from "../../../../redux/reducers/types";
import type {
  CreateOrganizationEventPayload,
  OrganizationEvent,
  OrganizationEventType,
} from "../../../../redux/reducers/types";
import { useOrganization } from "../../../hooks/useOrganization";
import {
  useCreateOrganizationEventMutation,
  useDeleteOrganizationEventMutation,
  useGetOrganizationEventsQuery,
  useUpdateOrganizationEventMutation,
} from "../../../../redux/reducers/apiSlice";
import { MESSAGE } from "../../../Constants/Message";

const EVENT_TYPE_LABELS: Record<OrganizationEventType, string> = {
  GATHERING: "Community gathering",
  WORKSHOP: "Workshop",
  MEETING: "Meeting",
  OTHER: "Other",
};

const emptyForm = (): CreateOrganizationEventPayload => ({
  title: "",
  description: "",
  eventType: "GATHERING",
  location: { city: "", state: "", isVirtual: false, details: "" },
  startsAt: "",
  endsAt: null,
});

const toDatetimeLocal = (iso?: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocal = (value: string) => {
  if (!value) return "";
  return new Date(value).toISOString();
};

const formatEventWhen = (startsAt: string, endsAt?: string | null) => {
  const start = new Date(startsAt);
  const startText = start.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  if (!endsAt) return startText;
  const end = new Date(endsAt);
  return `${startText} – ${end.toLocaleTimeString(undefined, { timeStyle: "short" })}`;
};

const formatLocation = (event: OrganizationEvent) => {
  const loc = event.location;
  if (!loc) return "—";
  if (loc.isVirtual) {
    return loc.details?.trim() ? `Virtual · ${loc.details}` : "Virtual";
  }
  const parts = [loc.city, loc.state].filter(Boolean);
  const place = parts.join(", ");
  if (place && loc.details) return `${place} · ${loc.details}`;
  return place || loc.details || "—";
};

type EventsProps = {
  organization?: Organization;
};

export const Events = ({ organization }: EventsProps) => {
  const { isCurrentUserAdmin } = useOrganization();
  const orgId = organization?.id ?? "";

  const { data: events = [], isLoading, isFetching } = useGetOrganizationEventsQuery(orgId, {
    skip: !orgId,
  });
  const [createEvent, { isLoading: creating }] = useCreateOrganizationEventMutation();
  const [updateEvent, { isLoading: updating }] = useUpdateOrganizationEventMutation();
  const [deleteEvent, { isLoading: deleting }] = useDeleteOrganizationEventMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OrganizationEvent | null>(null);
  const [form, setForm] = useState<CreateOrganizationEventPayload>(emptyForm());

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
      ),
    [events]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (event: OrganizationEvent) => {
    setEditing(event);
    setForm({
      title: event.title,
      description: event.description ?? "",
      eventType: event.eventType,
      location: {
        city: event.location?.city ?? "",
        state: event.location?.state ?? "",
        isVirtual: event.location?.isVirtual ?? false,
        details: event.location?.details ?? "",
      },
      startsAt: toDatetimeLocal(event.startsAt),
      endsAt: event.endsAt ? toDatetimeLocal(event.endsAt) : null,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!orgId) return;
    if (!form.title.trim()) {
      toast.error("Please enter an event title.");
      return;
    }
    if (!form.startsAt) {
      toast.error("Please choose a start date and time.");
      return;
    }

    const payload: CreateOrganizationEventPayload = {
      title: form.title.trim(),
      description: form.description?.trim() || "",
      eventType: form.eventType ?? "GATHERING",
      location: form.location,
      startsAt: fromDatetimeLocal(form.startsAt),
      endsAt: form.endsAt ? fromDatetimeLocal(form.endsAt) : null,
    };

    try {
      if (editing) {
        await updateEvent({
          organizationId: orgId,
          eventId: editing.id,
          event: payload,
        }).unwrap();
        toast.success("Event updated.");
      } else {
        await createEvent({ organizationId: orgId, event: payload }).unwrap();
        toast.success("Event logged.");
      }
      closeModal();
    } catch {
      toast.error(MESSAGE.SYSTEM_ERROR);
    }
  };

  const handleDelete = async (event: OrganizationEvent) => {
    if (!orgId || !window.confirm(`Remove "${event.title}"?`)) return;
    try {
      await deleteEvent({ organizationId: orgId, eventId: event.id }).unwrap();
      toast.success("Event removed.");
    } catch {
      toast.error(MESSAGE.SYSTEM_ERROR);
    }
  };

  if (!organization) {
    return (
      <Box py={6} textAlign="center" color="gray.600">
        Loading organization data...
      </Box>
    );
  }

  return (
    <Box py={2}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <HStack spacing={2}>
          <HiOutlineCalendar size={20} color="#EF6B2D" />
          <Text fontWeight="semibold">{sortedEvents.length} events logged</Text>
        </HStack>
        {isCurrentUserAdmin && (
          <Button colorScheme="orange" size="sm" onClick={openCreate}>
            Log event
          </Button>
        )}
      </HStack>

      <Text fontSize="sm" color="gray.600" mb={4}>
        Record community gatherings, workshops, and meetings your organization hosts. These
        count toward platform-wide participation on the Measure dashboard.
      </Text>

      {isLoading || isFetching ? (
        <Text color="gray.500" fontSize="sm">
          Loading events...
        </Text>
      ) : sortedEvents.length === 0 ? (
        <Box
          borderWidth="1px"
          borderRadius="md"
          borderColor="gray.200"
          py={10}
          px={4}
          textAlign="center"
        >
          <Text color="gray.600" mb={isCurrentUserAdmin ? 3 : 0}>
            No events logged yet.
          </Text>
          {isCurrentUserAdmin && (
            <Button colorScheme="orange" variant="outline" size="sm" onClick={openCreate}>
              Log your first event
            </Button>
          )}
        </Box>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Event</Th>
                <Th>Type</Th>
                <Th>When</Th>
                <Th>Location</Th>
                {isCurrentUserAdmin && <Th textAlign="right">Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {sortedEvents.map((event) => (
                <Tr key={event.id}>
                  <Td>
                    <Text fontWeight="medium">{event.title}</Text>
                    {event.description ? (
                      <Text fontSize="xs" color="gray.500" noOfLines={2}>
                        {event.description}
                      </Text>
                    ) : null}
                  </Td>
                  <Td>
                    <Badge colorScheme="orange" variant="subtle">
                      {EVENT_TYPE_LABELS[event.eventType] ?? event.eventType}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{formatEventWhen(event.startsAt, event.endsAt)}</Td>
                  <Td fontSize="sm">{formatLocation(event)}</Td>
                  {isCurrentUserAdmin && (
                    <Td textAlign="right">
                      <HStack justify="flex-end" spacing={2}>
                        <Button size="xs" variant="ghost" onClick={() => openEdit(event)}>
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          isLoading={deleting}
                          onClick={() => handleDelete(event)}
                        >
                          Remove
                        </Button>
                      </HStack>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <IgniteChakraModal isOpen={modalOpen} onClose={closeModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit event" : "Log event"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Neighborhood listening session"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={form.description ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes about who attended or outcomes"
                  rows={3}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Select
                  value={form.eventType ?? "GATHERING"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      eventType: e.target.value as OrganizationEventType,
                    }))
                  }
                >
                  {(Object.keys(EVENT_TYPE_LABELS) as OrganizationEventType[]).map((key) => (
                    <option key={key} value={key}>
                      {EVENT_TYPE_LABELS[key]}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Starts</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Ends (optional)</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.endsAt ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        endsAt: e.target.value || null,
                      }))
                    }
                  />
                </FormControl>
              </SimpleGrid>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Virtual event</FormLabel>
                <Switch
                  isChecked={form.location?.isVirtual ?? false}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      location: { ...f.location, isVirtual: e.target.checked },
                    }))
                  }
                />
              </FormControl>
              {!form.location?.isVirtual && (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      value={form.location?.city ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          location: { ...f.location, city: e.target.value },
                        }))
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input
                      value={form.location?.state ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          location: { ...f.location, state: e.target.value },
                        }))
                      }
                    />
                  </FormControl>
                </SimpleGrid>
              )}
              <FormControl>
                <FormLabel>
                  {form.location?.isVirtual ? "Meeting link or platform" : "Venue details"}
                </FormLabel>
                <Input
                  value={form.location?.details ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      location: { ...f.location, details: e.target.value },
                    }))
                  }
                  placeholder={
                    form.location?.isVirtual ? "Zoom link, etc." : "Community center room B"
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleSave}
              isLoading={creating || updating}
            >
              {editing ? "Save changes" : "Log event"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </IgniteChakraModal>
    </Box>
  );
};
